import React from 'react';
import { Descriptions, Tag, Tooltip, Avatar, Badge, Row, Col, Divider, Typography, Card, Grid } from "antd";
import { UserOutlined, MailOutlined, IdcardOutlined, SafetyCertificateOutlined, CalendarOutlined, GlobalOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { formatDate, getChannelIcon, getTokenExpiryColor, getTokenExpiryText } from '../CredentialTable/constants';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const CredentialDetailsModal = ({ credential, onCancel }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();

    if (!credential) return null;

    const tokenColor = getTokenExpiryColor(credential.token_expires_at, credential.is_permanent_token);
    const tokenText = getTokenExpiryText(credential.token_expires_at, credential.is_permanent_token, t);

    return (
        <ResponsiveModal
            title={t('credential_details')}
            open
            onCancel={onCancel}
            desktopWidth={600}
            footer={null}
        >
            {/* Header Section */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                <Badge
                    count={getChannelIcon(credential.channel_type, true)}
                    offset={[-10, 10]}
                >
                    <Avatar
                        src={credential.picture_url}
                        icon={<UserOutlined />}
                        size={84}
                        style={{ border: '2px solid #f0f0f0' }}
                    />
                </Badge>
                <Title level={4} style={{ marginTop: 16, marginBottom: 0 }}>
                    {credential.name || credential.first_name || 'Unnamed Credential'}
                </Title>
                <div style={{ marginTop: 8 }}>
                    <Tooltip title={t('token_status')}>
                        <Tag color={tokenColor} icon={<SafetyCertificateOutlined />}>
                            {tokenText}
                        </Tag>
                    </Tooltip>
                </div>
            </div>

            <Card bordered={false} style={{ background: '#fafafa' }}>
                {/* User Information */}
                <Divider orientation="left" style={{ marginTop: 0 }}>{t('user_info')}</Divider>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Descriptions column={1} size="small" colon={false} layout={screens.xs ? 'vertical' : 'horizontal'}>
                            {credential.email && (
                                <Descriptions.Item label={<SpaceLabel icon={<MailOutlined />} text={t('email')} />}>
                                    <Text copyable>{credential.email}</Text>
                                </Descriptions.Item>
                            )}
                            {credential.credential_user_id && (
                                <Descriptions.Item label={<SpaceLabel icon={<IdcardOutlined />} text={t('credential_user_id')} />}>
                                    <Text code copyable style={{ wordBreak: 'break-all' }}>{credential.credential_user_id}</Text>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={<SpaceLabel icon={<IdcardOutlined />} text={t('id')} />}>
                                <Text code style={{ wordBreak: 'break-all' }}>{credential.id}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>

                {/* Authentication & Timestamps */}
                <Divider orientation="left">{t('authentication')}</Divider>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Descriptions column={1} size="small" colon={false} layout={screens.xs ? 'vertical' : 'horizontal'}>
                            <Descriptions.Item label={t('is_permanent_token')}>
                                <Tag color={credential.is_permanent_token ? 'success' : 'default'}>
                                    {credential.is_permanent_token ? t('yes') : t('no')}
                                </Tag>
                            </Descriptions.Item>
                            {credential.token_expires_at && (
                                <Descriptions.Item label={<SpaceLabel icon={<CalendarOutlined />} text={t('token_expires_at')} />}>
                                    {formatDate(credential.token_expires_at)}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Col>
                </Row>

                <Divider orientation="left" style={{ fontSize: '12px', color: '#888', fontWeight: 'normal' }}>{t('metadata')}</Divider>
                <Row gutter={[0, 4]}>
                    <Col xs={24} sm={12}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('created_at')}: {formatDate(credential.created_at)}
                        </Text>
                    </Col>
                    {credential.updated_at && (
                        <Col xs={24} sm={12} style={{ textAlign: screens.xs ? 'left' : 'right' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {t('updated_at')}: {formatDate(credential.updated_at)}
                            </Text>
                        </Col>
                    )}
                </Row>
            </Card>
        </ResponsiveModal>
    );
};

const SpaceLabel = ({ icon, text }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {icon}
        {text}
    </span>
);

export default CredentialDetailsModal;
